import { AgentSessionStepType, AgentSessionType, type AgentSessionStep } from "@/modules/sessions/types";
import { ZTable } from "@/shared/components/table";
import { cn } from "@/shared/utils/classname-merger";
import { Spinner } from "@nextui-org/react";
import Fuse from "fuse.js";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, memo } from "react";
import {
  Control,
  CustomGapAssessmentControl,
  NistCsfControl,
  Soc2Control,
  useCustomizeControlsStore,
  VendorAssessmentControl
} from "../../../states/cusomize-controls";
import { filterListOfControlsBasedOnTabsAndAgentType } from "../CustomizeControlTopSection/CustomizeControlTopLeftSection/helper";
import { getRandomCharacter } from "../helpers";
import {
  customizeControlsColumns,
  gapCustomControlsColumns,
  nistCsfControlsColumns,
  searchAndFilteringList,
  updateHandlerBasedOnAgentTypes,
  vendorAssessmentControlsColumns,
} from "./helper";
import { useParams, useSearchParams } from "react-router-dom";
import { useAgentStepData, useReassessmentModeStore } from "@/modules/agent/states";
import { shouldShowEmptyRow, isEmptyControl } from "../../../use-cases/customize-control.use-case";
import { VirtualizedTable } from "@/shared/components/table/VirtualizedTable";
import { checkAndRefreshUrl } from "@/modules/agent/utils/refreshUrl";
import { addNotification } from "@/shared/states/notification";

// Type guard to check if a step is a customize controls step
function isCustomizeControlsStep(step: AgentSessionStep): step is AgentSessionStep & { type: AgentSessionStepType.CONTROL_SELECTION | AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING; data: { url: string | null; metadata?: any } } {
  return (step.type === AgentSessionStepType.CONTROL_SELECTION || step.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING) && 
         step.data && 
         typeof step.data === 'object' && 
         'url' in step.data;
}

// Create a context to hold showNistCheckboxes
interface NistCheckboxContextType {
  showCheckboxes: boolean;
  setShowCheckboxes: (show: boolean) => void;
}

export const NistCheckboxContext = createContext<NistCheckboxContextType>({
  showCheckboxes: false,
  setShowCheckboxes: () => { },
});

// Custom hook to use the checkbox context
export const useNistCheckboxContext = () => useContext(NistCheckboxContext);

const CustomizeControlsTable = () => {
  const {
    dataState,
    accordianOpenState,
    selectedControls,
    searchQuery,
    selectedTab,
    agentType,
    showCheckboxes,
    changesMade,
    newControlPlaceHolderUid: newControlPlaceHolderUidFromStore, // Get from store
    isCustomControls,
    activeBulkAction
  } = useCustomizeControlsStore();
  const {
    setDataState,
    setAccordianOpenState,
    setSelectedControls,
    setNewControlPlaceHolderUid, // Action to set/clear placeholder UID
    setChangesMade,
    setAgentType,
    setIsCustomControls, // Add this to enable custom controls mode for control sets
  } = useCustomizeControlsStore().actions;

  const { id } = useParams();
  const [searchParams] = useSearchParams();

  // Check if we're in control set edit mode - use store context instead of URL params
  const { controlSetEditContext } = useCustomizeControlsStore();
  const isControlSetEdit = controlSetEditContext?.isControlSetEdit || false;
  const controlSetId = controlSetEditContext?.controlSetId;
  const controlsUrl = controlSetEditContext?.controlsUrl;

  const stepData = useAgentStepData(id as string);

  // Get reassessment mode state
  const { isReassessmentMode, agentId: reassessmentAgentId } = useReassessmentModeStore();
  const isCurrentSessionInReassessment = isReassessmentMode && id === reassessmentAgentId;

  // Get the showNistCheckboxes value from context
  const { showCheckboxes: showNistCheckboxes } = useNistCheckboxContext();

  // Force refresh key when changes are made - especially important for RISK_ASSESSMENT
  const [refreshKey, setRefreshKey] = useState(0);
  const [isTableLoading, setIsTableLoading] = useState(true);

  // Track if focus has already been applied for the current placeholder
  const [focusApplied, setFocusApplied] = useState<string>('');
  
  // Control set specific state
  const [fetchingControlSetData, setFetchingControlSetData] = useState(false);

  // When changesMade is updated, update the refresh key to trigger re-render
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [changesMade]);

  // Control set data fetching function
  const fetchControlSetData = async (url: string) => {
    setFetchingControlSetData(true);
    try {
      // Use the same URL refresh logic as CreateControlSetModal
      const validSignedUrl = await checkAndRefreshUrl(url);
      
      // Fetch controls data directly from blob storage
      const response = await fetch(validSignedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch controls: ${response.statusText}`);
      }
      
      const controlsData = await response.json();
      
      // Transform the data to match VendorAssessmentControl interface
      const transformedControls: VendorAssessmentControl[] = controlsData.map((control: any) => ({
        ...control,
        agentType: AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT,
        uid: control.uid || control.id || `control_${Math.random().toString(36).substr(2, 9)}`,
        category: control.category || control.key || '',
        question: control.question || '',
        section: control.section || 'zania_security_assessment' as const,
        edited_state: control.edited_state || 'default' as const,
        changesInQuestion: control.changesInQuestion || 0,
      }));
      
      // Set the data in store
      setDataState(transformedControls);
      setAgentType(AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT);
      setIsCustomControls(true); // Enable custom controls mode to show empty row
      
    } catch (error) {
      console.error('Error fetching control set data:', error);
      addNotification({
        type: "error",
        title: "Failed to load control set",
        message: "Could not fetch control set data. Please try again.",
      });
    } finally {
      setFetchingControlSetData(false);
    }
  };

  // Effect to load control set data when in edit mode
  useEffect(() => {
    if (isControlSetEdit && controlsUrl) {
      fetchControlSetData(controlsUrl);
    }
  }, [isControlSetEdit, controlsUrl]);

  const dataStateBasedOnSelected = useMemo(
    () =>
      agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT || agentType === AgentSessionType.GAP_CUSTOM_FRAMEWORK
        ? dataState // Show all controls for vendor assessment
        : dataState.filter((c) =>
          filterListOfControlsBasedOnTabsAndAgentType({
            agentType,
            control: c,
            selectedTab,
          })
        ),
    [dataState, selectedTab, agentType, refreshKey]
  );

  const fuse = useMemo(() => {
    return new Fuse(dataStateBasedOnSelected, {
      keys: searchAndFilteringList(agentType),
      threshold: 0.2,
      ignoreLocation: true,
      useExtendedSearch: true,
    });
  }, [dataStateBasedOnSelected, agentType]);

  const filteredRows = useMemo(() => {
    return searchQuery
      ? fuse
        .search(searchQuery)
        .map((result) => result.item)
        .filter(Boolean)
      : dataStateBasedOnSelected;
  }, [dataStateBasedOnSelected, fuse, searchQuery, agentType]);

  // Set isTableLoading based on data presence

  const sectionWiseData = useMemo(() => {
    const baseArrayForOutput =
      agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT || agentType === AgentSessionType.GAP_CUSTOM_FRAMEWORK
        ? filteredRows
        : filteredRows.filter((control) =>
          filterListOfControlsBasedOnTabsAndAgentType({
            agentType,
            control,
            selectedTab,
          })
        );

    const output = [...baseArrayForOutput];

    // Use the new use case to determine if we should show an empty row
    const shouldAddEmptyRow = shouldShowEmptyRow(agentType, isCustomControls, searchQuery, showCheckboxes, isCurrentSessionInReassessment);

    if (shouldAddEmptyRow) {
      // Check if there's already an empty row to avoid duplicates
      const hasEmptyRow = output.some(control => isEmptyControl(control, agentType));

      if (!hasEmptyRow) {
        // Add empty row based on agent type
        if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
          const uid = `empty_vendor_${getRandomCharacter()}`;
          output.push({
            uid,
            edited_state: "NEW",
            category: "",
            question: "",
            agentType: AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT,
            section: "zania_security_assessment",
          } as VendorAssessmentControl);
        }
        else if (agentType === AgentSessionType.SOC2_GAP_TYPE2) {
          const uid = `empty_soc2_type2_${getRandomCharacter()}`;
          output.push({
            tsc_id: "",
            uid,
            edited_state: "NEW",
            category: "",
            toe: "",
            tod: "",
            control: "",
            control_id: "",
            criteria: "",
            tsc: selectedTab,
            zania_control_id: "",
            agentType: AgentSessionType.SOC2_GAP_TYPE2,
          } as Soc2Control);
        }
        else if (agentType === AgentSessionType.SOC2_GAP_TYPE1) {
          const uid = `empty_soc2_type1_${getRandomCharacter()}`;
          output.push({
            tsc_id: "",
            uid,
            edited_state: "NEW",
            category: "",
            toe: "",
            tod: "",
            control: "",
            control_id: "",
            criteria: "",
            tsc: selectedTab,
            zania_control_id: "",
            agentType: AgentSessionType.SOC2_GAP_TYPE1,
          } as Soc2Control);
        }
        else if (agentType === AgentSessionType.GAP_CUSTOM_FRAMEWORK) {
          const customizeControlStep = stepData?.find(x => x.type === AgentSessionStepType.CONTROL_SELECTION);
          const controlSelectionStepDetails = (customizeControlStep && isCustomizeControlsStep(customizeControlStep)) ? customizeControlStep.data.metadata : undefined;

          const allRequiredColumns = controlSelectionStepDetails?.reduce((acc: any, curr: any) => {
            acc[curr.key] = "";
            return acc;
          }, {}) || {};

          const uid = getRandomCharacter();
          const controlIdPrefix = (output?.[0] as CustomGapAssessmentControl)?.controlIdPrefix || 'C';
          output.push({
            ...allRequiredColumns,
            ["Control Id"]: "",
            ["Control Description"]: "",
            control_description: "",
            control: "",
            tod: "",
            ["Testing Procedures"]: "",
            testing_procedures: "",
            toe: "",
            control_id: "",
            agentType: AgentSessionType.GAP_CUSTOM_FRAMEWORK,
            uid,
            uuid: uid,
            edited_state: "NEW",
            controlIdPrefix,
            category: "",
            criteria: "",
            tsc: selectedTab,
            zania_control_id: "",
          } as any);
        }
      }
    }

    // Handle placeholder for other agent types only if newControlPlaceHolderUidFromStore is set
    // and no search query and customization is enabled
    if (searchQuery.trim().length === 0 && newControlPlaceHolderUidFromStore && isCustomControls) {
      const uid = newControlPlaceHolderUidFromStore;
      switch (agentType) {
        case AgentSessionType.SOC2_GAP:
          output.push({
            tsc_id: "",
            uid,
            edited_state: "NEW",
            category: "", 
            toe: "", 
            tod: "", 
            control: "", 
            control_id: "", 
            criteria: "", 
            tsc: selectedTab, 
            zania_control_id: "",
            agentType: agentType,
          } as Soc2Control);
          break;
        // Other agent types can be added here if needed
      }
    }

    return output.map((d) => ({ ...d, id: d.uid }));
  }, [filteredRows, selectedTab, searchQuery, agentType, refreshKey, newControlPlaceHolderUidFromStore, showCheckboxes, isCustomControls, stepData, isCurrentSessionInReassessment]);

  useEffect(() => {
    // Show loading while fetching control set data
    if (isControlSetEdit && fetchingControlSetData) {
      setIsTableLoading(true);
      return;
    }

    if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT || agentType === AgentSessionType.SOC2_GAP_TYPE1 || AgentSessionType.SOC2_GAP_TYPE2) {
      // For vendor assessment, generally set loading to false.
      // If data arrays are empty, the table's emptyContent will be shown.
      setIsTableLoading(false);
    } else {
      // Original logic for other agent types
      if (dataStateBasedOnSelected.length > 0 || filteredRows.length > 0 || sectionWiseData.length > 0) {
        setIsTableLoading(false);
      } else {
        setIsTableLoading(true);
      }
    }
  }, [dataStateBasedOnSelected, filteredRows, sectionWiseData, agentType, isControlSetEdit, fetchingControlSetData]);

  // ADD new useEffect for focusing the placeholder when it's set
  useEffect(() => {
    if (newControlPlaceHolderUidFromStore && agentType && newControlPlaceHolderUidFromStore !== focusApplied) {
      let prefix = "";
      switch (agentType) {
        case AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT: prefix = "key"; break;
        case AgentSessionType.SOC2_GAP:
        case AgentSessionType.SOC2_GAP_TYPE1:
        case AgentSessionType.SOC2_GAP_TYPE2:
          prefix = "tsc_id"; break;
        // Add other agent types if needed
      }

      // Ensure the element ID is constructed correctly based on your column rendering
      // It should match the ID of the input field you want to focus.
      const elementId = `${newControlPlaceHolderUidFromStore}${prefix}`;
      const el = document.getElementById(elementId);

      if (el && document.activeElement !== el) {
        // Only focus if the element is not already focused (prevents interrupting typing)
        setTimeout(() => {
          // Double check the element is not focused before applying focus
          if (document.activeElement !== el) {
            el.focus();
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
            setFocusApplied(newControlPlaceHolderUidFromStore);
          }
        }, 0);
      } else if (el && document.activeElement === el) {
        // Mark as applied if already focused
        setFocusApplied(newControlPlaceHolderUidFromStore);
      }
    }
  }, [newControlPlaceHolderUidFromStore, agentType, sectionWiseData, focusApplied]);

  // Reset focus tracking when placeholder UID changes or is cleared
  useEffect(() => {
    if (!newControlPlaceHolderUidFromStore) {
      setFocusApplied('');
    }
  }, [newControlPlaceHolderUidFromStore]);

  /*
  // This useEffect is a likely cause of multiple empty rows if setSectionWiseData
  // inadvertently updates dataState or a source for dataState with the placeholder.
  // The sectionWiseData computed here is for local table rendering and includes a
  // transient placeholder. Storing it back globally can create a feedback loop.
  // useEffect(() => {
  //   getCustomizeControlsActions().setSectionWiseData(sectionWiseData);
  // }, [sectionWiseData]);
  */

  const handleChangeDataState = useCallback(
    (
      agentType: AgentSessionType | null,
      key: keyof Soc2Control | keyof VendorAssessmentControl | keyof NistCsfControl,
      uId: string,
      newValue: string,
      changesInTestingProcedure?: number // This is 'changesInControl' for NIST
    ) => {
      // For MULTI_FILE_VENDOR_ASSESSMENT, handle the persistent empty row logic
      if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
        const foundControl = sectionWiseData.find(c => c.uid === uId);
        const isNewRow = foundControl?.edited_state === "NEW";
        const isEmptyRow = foundControl?.agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT &&
          !(foundControl as VendorAssessmentControl).category &&
          !(foundControl as VendorAssessmentControl).question;

        if (isNewRow && isEmptyRow) {
          // This is editing the empty row, just update it in place without changing UID
          setDataState((prevDataState) => {
            const placeholderData = sectionWiseData.find(c => c.uid === uId);
            if (!placeholderData || placeholderData.agentType !== AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) return prevDataState;

            // Check if this control already exists in dataState
            const existingControlIndex = prevDataState.findIndex(c => c.uid === uId);

            if (existingControlIndex >= 0) {
              // Update existing control
              return prevDataState.map(control =>
                control.uid === uId
                  ? { ...control, [key as string]: newValue, edited_state: "custom" as const }
                  : control
              );
            } else {
              // Add new control to dataState
              const newControlToAdd: VendorAssessmentControl = {
                ...(placeholderData as VendorAssessmentControl),
                [key as string]: newValue,
                edited_state: "custom",
              };
              return [...prevDataState, newControlToAdd];
            }
          });
          // Skip auto-save for AI-generated fields (new_*)
          if (!key.startsWith('new_')) {
            setChangesMade((prev) => prev + 1);
          }
          return;
        }
      }

      // For SOC2_GAP_TYPE2, handle the persistent empty row logic
      if (agentType === AgentSessionType.SOC2_GAP_TYPE2) {
        const foundControl = sectionWiseData.find(c => c.uid === uId);
        const isNewRow = foundControl?.edited_state === "NEW";
        const isEmptyRow = foundControl?.agentType === AgentSessionType.SOC2_GAP_TYPE2 &&
          !(foundControl as Soc2Control).tsc_id &&
          !(foundControl as Soc2Control).control;

        if (isNewRow && isEmptyRow) {
          setDataState((prevDataState) => {
            const placeholderData = sectionWiseData.find(c => c.uid === uId);
            if (!placeholderData || placeholderData.agentType !== AgentSessionType.SOC2_GAP_TYPE2) return prevDataState;

            const existingControlIndex = prevDataState.findIndex(c => c.uid === uId);

            if (existingControlIndex >= 0) {
              // Update both tsc_id and control_id if tsc_id is being changed
              return prevDataState.map(control =>
                control.uid === uId
                  ? key === "tsc_id"
                    ? { ...control, tsc_id: newValue, control_id: newValue, edited_state: "custom" as const }
                    : { ...control, [key as string]: newValue, edited_state: "custom" as const }
                  : control
              );
            } else {
              // Add new control to dataState
              const newControlToAdd: Soc2Control =
                key === "tsc_id"
                  ? { ...(placeholderData as Soc2Control), tsc_id: newValue, control_id: newValue, edited_state: "custom" }
                  : { ...(placeholderData as Soc2Control), [key as string]: newValue, edited_state: "custom" };
              return [...prevDataState, newControlToAdd];
            }
          });
          if (!key.startsWith('new_')) {
            setChangesMade((prev) => prev + 1);
          }
          return;
        }
      }
      // Repeat the same logic for SOC2_GAP_TYPE1
      if (agentType === AgentSessionType.SOC2_GAP_TYPE1) {
        const foundControl = sectionWiseData.find(c => c.uid === uId);
        const isNewRow = foundControl?.edited_state === "NEW";
        const isEmptyRow = foundControl?.agentType === AgentSessionType.SOC2_GAP_TYPE1 &&
          !(foundControl as Soc2Control).tsc_id &&
          !(foundControl as Soc2Control).control;

        if (isNewRow && isEmptyRow) {
          setDataState((prevDataState) => {
            const placeholderData = sectionWiseData.find(c => c.uid === uId);
            if (!placeholderData || placeholderData.agentType !== AgentSessionType.SOC2_GAP_TYPE1) return prevDataState;

            const existingControlIndex = prevDataState.findIndex(c => c.uid === uId);

            if (existingControlIndex >= 0) {
              return prevDataState.map(control =>
                control.uid === uId
                  ? key === "tsc_id"
                    ? { ...control, tsc_id: newValue, control_id: newValue, edited_state: "custom" as const }
                    : { ...control, [key as string]: newValue, edited_state: "custom" as const }
                  : control
              );
            } else {
              const newControlToAdd: Soc2Control =
                key === "tsc_id"
                  ? { ...(placeholderData as Soc2Control), tsc_id: newValue, control_id: newValue, edited_state: "custom" }
                  : { ...(placeholderData as Soc2Control), [key as string]: newValue, edited_state: "custom" };
              return [...prevDataState, newControlToAdd];
            }
          });
          if (!key.startsWith('new_')) {
            setChangesMade((prev) => prev + 1);
          }
          return;
        }
      }

      if (agentType === AgentSessionType.GAP_CUSTOM_FRAMEWORK) {
        const foundControl = sectionWiseData.find(c => c.uid === uId);
        const isNewRow = foundControl?.edited_state === "NEW";
        const isEmptyRow = foundControl?.agentType === AgentSessionType.GAP_CUSTOM_FRAMEWORK &&
          !(foundControl).control_id &&
          !(foundControl).control;

        if (isNewRow && isEmptyRow) {
          // This is editing the empty row, just update it in place without changing UID
          setDataState((prevDataState: any) => {
            const placeholderData = sectionWiseData.find(c => c.uid === uId);
            if (!placeholderData || placeholderData.agentType !== AgentSessionType.GAP_CUSTOM_FRAMEWORK) return prevDataState;

            // Check if this control already exists in dataState
            const existingControlIndex = prevDataState.findIndex((c: any) => c.uid === uId);

            if (existingControlIndex >= 0) {
              // Update existing control
              return prevDataState.map((control: any) =>
                control.uid === uId
                  ? { ...control, [key as string]: newValue, edited_state: "custom" as const }
                  : control
              );
            } else {
              // Add new control to dataState
              const newControlToAdd = {
                ...(placeholderData),
                [key as string]: newValue,
                edited_state: "custom",
              };
              return [...prevDataState, newControlToAdd];
            }
          });
          // Skip auto-save for AI-generated fields (new_*)
          if (!key.startsWith('new_')) {
            setChangesMade((prev) => prev + 1);
          }
          return;
        }
      }

      // Check if the control being edited is the active placeholder for other agent types
      if (uId === newControlPlaceHolderUidFromStore && agentType !== AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT && agentType !== AgentSessionType.SOC2_GAP_TYPE2 && agentType !== AgentSessionType.SOC2_GAP_TYPE1) {
        const placeholderData = sectionWiseData.find(c => c.uid === uId);
        if (!placeholderData) return;

        setDataState((prevDataState) => {
          const newControlToAdd = {
            ...placeholderData,
            [key as string]: newValue,
            edited_state: "custom" as Control["edited_state"],
          };

          if (agentType === AgentSessionType.RISK_ASSESSMENT && typeof changesInTestingProcedure === 'number') {
            (newControlToAdd as NistCsfControl).changesInControl = changesInTestingProcedure;
          }

          return [...prevDataState, newControlToAdd];
        });
        // Skip auto-save for AI-generated fields (new_*)
        if (!key.startsWith('new_')) {
          setChangesMade((prev) => prev + 1);
        }
        setNewControlPlaceHolderUid("");
      } else {
        // It's an existing control, update it using the existing mechanism
        switch (agentType) {
          case AgentSessionType.SOC2_GAP_TYPE1:
          case AgentSessionType.SOC2_GAP_TYPE2:
          case AgentSessionType.SOC2_GAP:
            updateHandlerBasedOnAgentTypes({
              agentType,
              key: key as keyof Soc2Control,
              uId,
              newValue,
              changesInTestingProcedure,
            });
            break;
          case AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT:
            updateHandlerBasedOnAgentTypes({
              agentType,
              key: key as keyof VendorAssessmentControl,
              uId,
              newValue,
              changesInTestingProcedure,
            });
            break;
          case AgentSessionType.RISK_ASSESSMENT:
            updateHandlerBasedOnAgentTypes({
              agentType,
              key: key as keyof NistCsfControl,
              uId,
              newValue,
              changesInControl: changesInTestingProcedure,
            });
            break;
          case AgentSessionType.GAP_CUSTOM_FRAMEWORK:
            updateHandlerBasedOnAgentTypes({
              agentType,
              key: key as keyof Soc2Control,
              uId,
              newValue,
              changesInTestingProcedure,
            });
            break;
          default:
            break;
        }
      }
    },
    [setDataState, newControlPlaceHolderUidFromStore, sectionWiseData, setChangesMade, setNewControlPlaceHolderUid]
  );

  const columns = useMemo(() => {
    switch (agentType) {
      case AgentSessionType.SOC2_GAP_TYPE1:
      case AgentSessionType.SOC2_GAP_TYPE2:
      case AgentSessionType.SOC2_GAP:
        return customizeControlsColumns({
          accordianOpenState,
          filteredRows: filteredRows as Soc2Control[],
          handleChangeDataState: (
            key: keyof Soc2Control,
            uId: string,
            newValue: string,
            changesInTestingProcedure?: number
          ) =>
            handleChangeDataState(
              agentType,
              key,
              uId,
              newValue,
              changesInTestingProcedure
            ),
          searchQuery,
          selectedControls,
          setAccordianOpenState,
          setSelectedControls,
          showCheckboxes, // Pass showCheckboxes to SOC2 columns
          isDisabled: !isCustomControls,
          activeBulkAction,
          agentId: id,
          isReassessmentMode: isCurrentSessionInReassessment,
        });

      case AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT:
        return vendorAssessmentControlsColumns({
          handleChangeDataState: (
            key: keyof VendorAssessmentControl,
            uId: string,
            newValue: string,
            changesInTestingProcedure?: number
          ) =>
            handleChangeDataState(
              agentType,
              key,
              uId,
              newValue,
              changesInTestingProcedure
            ),
          searchQuery,
          selectedControls,
          setSelectedControls,
          filteredRows: filteredRows as VendorAssessmentControl[],
          showCheckboxes,
          isDisabled: false, // Always enable inputs in control set edit mode
        });

      case AgentSessionType.RISK_ASSESSMENT:
        return nistCsfControlsColumns({
          handleChangeDataState: (
            key: string,
            uId: string,
            newValue: string,
            changesInControl?: number
          ) =>
            handleChangeDataState(
              agentType,
              key as any,
              uId,
              newValue,
              changesInControl
            ),
          searchQuery,
          selectedControls,
          setSelectedControls,
          filteredRows: filteredRows as any[],
          selectedTab,
          showCheckboxes: showNistCheckboxes,
        });

      case AgentSessionType.GAP_CUSTOM_FRAMEWORK:
        return gapCustomControlsColumns({
          accordianOpenState,
          filteredRows: filteredRows as Soc2Control[],
          handleChangeDataState: (
            key: keyof Soc2Control,
            uId: string,
            newValue: string,
            changesInTestingProcedure?: number
          ) =>
            handleChangeDataState(
              agentType,
              key,
              uId,
              newValue,
              changesInTestingProcedure
            ),
          searchQuery,
          selectedControls,
          setAccordianOpenState,
          setSelectedControls,
          showCheckboxes, // Pass showCheckboxes to SOC2 columns
          isDisabled: !isCustomControls,
          activeBulkAction,
          agentId: id,
          isReassessmentMode: isCurrentSessionInReassessment,
        });

      default:
        return [];
    }
  }, [
    agentType,
    accordianOpenState,
    filteredRows,
    handleChangeDataState,
    searchQuery,
    selectedControls,
    setAccordianOpenState,
    setSelectedControls,
    selectedTab,
    showCheckboxes,
    showNistCheckboxes,
    refreshKey
  ]);


  if(agentType === AgentSessionType.GAP_CUSTOM_FRAMEWORK){

  return (
    <div className="grow overflow-hidden flex border-t  ">
      <div
        id="customize-control-table"
        className="flex-1 flex flex-col bg-white overflow-auto"
      >
        {
          isTableLoading || fetchingControlSetData ? (
            <div className="flex flex-1 items-center justify-center min-h-[300px]">
              <Spinner />
            </div>
          ) : (
            <VirtualizedTable<Control>
              aria-label="Customize Controls list"
              isCompact={true}
              removeWrapper={true}
              columns={columns.length ? columns : [{}] as any} // todo: later lets try to fix it.
              tableRowProps={(control) => {
                return {
                  className: cn(
                    "border-t-1 border-b-1 border-[#E4E4E7] bg-white pb-1 group",
                    "min-h-[36px]",
                    control.edited_state === "ACCORDIAN" && "!bg-zinc-100",
                    control.edited_state === "NEW" && "!bg-zinc-50"
                  ),
                };
              }}
              tableBodyProps={{
                emptyContent: isTableLoading ? <Spinner /> : "No control found",
              }}
              tableColumnProps={({ fieldName }) => ({
                className: cn(
                  fieldName === "control" && "pl-[4px]",
                  // Apply specific width constraints for each column
                  fieldName === "id" && "w-[100px] max-w-[120px]",
                  fieldName === "control" && "min-w-0", // Allow control column to shrink
                  fieldName === "testing_procedure" && "w-[280px] max-w-[280px]",
                  fieldName === "function" && "min-w-0", // Allow function column to shrink
                  fieldName === "control_id" && "w-[80px] max-w-[80px]",
                  fieldName === "documents" && "w-[80px] max-w-[80px]"
                ),
              })}
              classNames={{
                tr: "hover:bg-[#ECECEC] !rounded-none ",
                th: "bg-[#F9F7F7] !rounded-none",
                td: "px-1", // Add 4px horizontal padding (1 = 4px in Tailwind)
                table: "h-full"
              }}
              tableHeaderProps={{
                className: "!rounded-none",
              }}
              className="static h-full block"
              tableCellProps={() => ({ className: cn("align-baseline px-1") })} // Add 4px padding to cells
              rows={sectionWiseData}
              isHeaderSticky={true}
              showCheckboxes={showCheckboxes}
            />
          )
        }
      </div>
    </div>
  )
}


return (
    <div className="grow overflow-hidden flex border-t  ">
      <div
        id="customize-control-table"
        className="flex-1 flex flex-col bg-white overflow-auto"
      >
        {
          isTableLoading ? (
            <div className="flex flex-1 items-center justify-center min-h-[300px]">
              <Spinner />
            </div>
          ) : (
            <ZTable<Control>
              aria-label="Customize Controls list"
              isCompact={true}
              removeWrapper={true}
              columns={columns.length ? columns : [{}] as any} // todo: later lets try to fix it.
              tableRowProps={(control) => {
                return {
                  className: cn(
                    "border-t-1 border-b-1 border-[#E4E4E7] bg-white pb-1 group",
                    "min-h-[36px]",
                    control.edited_state === "ACCORDIAN" && "!bg-zinc-100",
                    control.edited_state === "NEW" && "!bg-zinc-50"
                  ),
                };
              }}
              tableBodyProps={{
                emptyContent: isTableLoading ? <Spinner /> : "No control found",
              }}
              tableColumnProps={({ fieldName }) => ({
                className: cn(
                  fieldName === "control" && "pl-[4px]",
                  // Apply specific width constraints for each column
                  fieldName === "id" && "w-[100px] max-w-[120px]",
                  fieldName === "category" && agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT && "w-[240px] max-w-[240px]", // Set to 240px
                  fieldName === "category" && (agentType === AgentSessionType.SOC2_GAP || agentType === AgentSessionType.SOC2_GAP_TYPE1 || agentType === AgentSessionType.SOC2_GAP_TYPE2) && "w-[200px] max-w-[200px]",
                  fieldName === "category" && agentType === AgentSessionType.RISK_ASSESSMENT && "w-[200px] max-w-[200px]",
                  fieldName === "control" && "min-w-0", // Allow control column to shrink
                  fieldName === "testing_procedure" && "w-[280px] max-w-[280px]",
                  fieldName === "function" && "min-w-0", // Allow function column to shrink
                  fieldName === "control_id" && "w-[80px] max-w-[80px]",
                  fieldName === "documents" && "w-[80px] max-w-[80px]"
                ),
              })}
              classNames={{
                tr: "hover:bg-[#ECECEC] !rounded-none ",
                th: "bg-[#F9F7F7] !rounded-none",
                td: "px-1", // Add 4px horizontal padding (1 = 4px in Tailwind)
                table: "h-full"
              }}
              tableHeaderProps={{
                className: "!rounded-none",
              }}
              className="static h-full block"
              tableCellProps={() => ({
                className: cn(
                  "align-baseline px-1",
                  (agentType === AgentSessionType.SOC2_GAP ||
                    agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
                    agentType === AgentSessionType.SOC2_GAP_TYPE2 ||
                    agentType === (AgentSessionType as any).GAP_CUSTOM_FRAMEWORK) && "border-r border-gray-200"
                )
              })} // Add 4px padding to cells
              rows={sectionWiseData}
              isHeaderSticky={true}
              showCheckboxes={showCheckboxes}
            />
          )
        }
      </div>
    </div>
  );
};

export default memo(CustomizeControlsTable);
